import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ROLES, ROLE_HINT, ROLE_LABEL, type Role } from '../../features/workspaces/api';
import { useSelectedWorkspace, useWorkspaceStore } from '../../stores/workspaceStore';
import { useSession } from '../layout/TopBar';
import { useCollections } from '../api/collections';
import { formatBytes, useMembers, useUsage, useWorkspaceMutations } from '../api/account';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { confirm } from '../ui/Confirm';
import { Badge } from '../ui/Controls';
import { IconX, IconStar } from '../ui/icons';
import { InlineTitle } from '../ui/Inline';
import { Select } from '../ui/Select';
import { toast } from '../ui/toast';

/**
 * Workspace (`is.workspace`): the name (click to rename), Default badge or
 * "Make default", the invite box, the People table with a role Select per
 * row and Remove. `?new=1` creates a workspace and opens its name for
 * editing; `?invite=1` focuses the invite field (the top bar's Invite).
 *
 * The OWNER (whose storage the workspace counts against, server
 * catalog/storage.ts) is marked in the table. They stay an administrator and
 * a member, and only they can hand it to another administrator ("Make
 * owner"), which the server refuses when it does not fit that person's free
 * storage.
 *
 * Roles follow the server (viewer, member, workspace_admin); the labels and
 * the hint under each come from features/workspaces/api, which both shells
 * read — they used to be spelled here and again there, and disagreed.
 */
const ROLE_OPTIONS: Array<{ value: Role; label: string }> = ROLES.map((value) => ({ value, label: ROLE_LABEL[value] }));
const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

export function WorkspacePage() {
  const [params, setParams] = useSearchParams();
  const { data: me } = useSession();
  const { workspaces, current } = useSelectedWorkspace();
  const setSelected = useWorkspaceStore((s) => s.setSelected);
  const members = useMembers(current?.public_id);
  // The owner's own figures, for what a hand-over moves (their Usage page's read).
  const usage = useUsage(!!current?.is_owner);
  const collections = useCollections({ limit: 1 });
  const m = useWorkspaceMutations();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const inviteRef = useRef<HTMLInputElement>(null);

  // ?new=1: a fresh workspace, selected, its name open for editing.
  const creating = useRef(false);
  useEffect(() => {
    if (params.get('new') !== '1' || creating.current) return;
    creating.current = true;
    const p = new URLSearchParams(params);
    p.delete('new');
    setParams(p, { replace: true });
    const taken = new Set(workspaces.map((w) => w.name));
    let name = 'New workspace';
    for (let i = 2; taken.has(name); i++) name = `New workspace ${i}`;
    m.create.mutate(name, {
      onSuccess: (w) => {
        setSelected(w.public_id);
        setEditing(true);
        creating.current = false;
      },
      onError: () => {
        creating.current = false;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => {
    if (params.get('invite') === '1') {
      inviteRef.current?.focus();
      const p = new URLSearchParams(params);
      p.delete('invite');
      setParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, current?.public_id]);

  if (!current) return <div style={{ flex: 1 }} />;
  const ws = current;
  const admin = ws.role === 'workspace_admin';
  const people = members.data?.members ?? [];
  const invites = members.data?.invitations ?? [];
  const summary = `${people.length} ${people.length === 1 ? 'person' : 'people'}${invites.length ? ` · ${invites.length} invited` : ''}`;
  const owner = people.find((p) => p.is_owner);
  const ownerLine = ws.is_owner ? 'You own it: its storage counts against yours.' : owner ? `Owned by ${owner.display_name || owner.email}.` : '';
  // The owner sees a column to hand it over; nobody else has anything to put there.
  const cols = ws.is_owner ? '44px minmax(0,1fr) 104px 150px 40px' : '44px minmax(0,1fr) 150px 40px';
  const held = usage.data?.workspaces.find((w) => w.public_id === ws.public_id);
  const handOver = async (p: { user_id: string; display_name: string; email: string | null }) => {
    const who = p.display_name || p.email || 'them';
    const size = held ? ` (${formatBytes(held.usedBytes + held.pendingBytes)})` : '';
    const ok = await confirm({
      title: `Make ${who} the owner of ${ws.name}?`,
      body: `Its storage${size} will count against theirs instead of yours. You stay an administrator; only they can hand it on after this.`,
      action: 'Make owner',
    });
    if (ok) m.transfer.mutate({ id: ws.public_id, userId: p.user_id, name: who });
  };
  const valid = emailOk(email) && admin;
  const send = () => {
    if (!valid) return;
    m.invite.mutate({ id: ws.public_id, email: email.trim(), role }, { onSuccess: () => setEmail('') });
  };

  return (
    <div className="tr-page" style={{ padding: '36px 28px 44px', display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 960, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InlineTitle
              value={ws.name}
              editing={editing}
              onStart={() => admin && setEditing(true)}
              onCommit={(v) => {
                setEditing(false);
                if (v.trim() && v.trim() !== ws.name) m.rename.mutate({ id: ws.public_id, name: v.trim() });
              }}
              onCancel={() => setEditing(false)}
              size={26}
              placeholder="Workspace name"
              minWidth={320}
              title={admin ? 'Click to rename' : undefined}
            />
            {ws.is_default ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, height: 22, padding: '0 8px', borderRadius: 5, border: '1px solid #e3e3e1', color: '#6b6b70', fontSize: 11.5, fontWeight: 500 }}>
                <IconStar size={11} fill="currentColor" sw={1.5} />
                Default
              </span>
            ) : null}
          </div>
          <p style={{ margin: 0, fontSize: 14, color: '#6b6b70', lineHeight: 1.5, maxWidth: 560 }}>
            {summary} · {collections.data?.total ?? 0} collections. Everyone here can see all collections in this workspace, including private ones. {ownerLine}
          </p>
        </div>
        {!ws.is_default && ws.role !== 'viewer' ? (
          <Button variant="outlined" size={32} icon={<IconStar size={13} />} onClick={() => m.makeDefault.mutate(ws.public_id, { onSuccess: () => toast(`${ws.name} is now your default workspace`) })}>
            Make default
          </Button>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 22px', border: '1px solid #ebebea', borderRadius: 12, background: '#fafaf9' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Invite people</span>
          <span style={{ fontSize: 13, color: '#6b6b70' }}>They'll get an email with a link to join this workspace.</span>
        </div>
        <div className="tr-wrap-sm" style={{ display: 'flex', gap: 8 }}>
          <input
            className="tr-basis-full-sm"
            ref={inviteRef}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="you@example.com"
            disabled={!admin}
            style={{ flex: 1, height: 36, padding: '0 12px', border: '1px solid #e3e3e1', borderRadius: 7, fontSize: 13.5, fontFamily: 'inherit', outline: 'none', minWidth: 0, background: '#fff', color: '#0f0f10' }}
          />
          <Select value={role} options={ROLE_OPTIONS} onChange={(v) => setRole(v as Role)} width={164} disabled={!admin} />
          <button
            type="button"
            onClick={send}
            style={{ height: 36, padding: '0 14px', border: 0, borderRadius: 7, background: valid ? '#0f0f10' : '#e3e3e1', color: valid ? '#fff' : '#8a8a8f', fontSize: 13, fontWeight: 500, cursor: valid ? 'pointer' : 'default', whiteSpace: 'nowrap', fontFamily: 'inherit' }}
          >
            Send invite
          </button>
        </div>
        <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.45 }}>{admin ? ROLE_HINT[role] : 'Only administrators can invite people to this workspace.'}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>People</h2>
          <span style={{ fontSize: 13, color: '#8a8a8f' }}>{summary}</span>
        </div>
        <div className="tr-table-480" style={{ border: '1px solid #ebebea', borderRadius: 10, overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', height: 40, padding: '0 16px', background: '#fafaf9', borderBottom: '1px solid #ebebea', fontSize: 12, fontWeight: 500, color: '#6b6b70', gap: 16 }}>
            <div />
            <div>Name</div>
            {ws.is_owner ? <div /> : null}
            <div>Role</div>
            <div />
          </div>
          {people.map((p) => {
            const you = p.user_id === me?.userId;
            return (
              <div key={p.user_id} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0ee', gap: 16 }}>
                <Avatar name={p.display_name || p.email || ''} size={32} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 500 }}>{p.display_name || p.email}</span>
                    {you ? <span style={{ fontSize: 11, color: '#8a8a8f' }}>you</span> : null}
                    {p.is_owner ? <Badge>OWNER</Badge> : null}
                  </div>
                  <span className="tr-ellipsis" style={{ fontSize: 12.5, color: '#8a8a8f' }}>{p.email}</span>
                </div>
                {ws.is_owner ? (
                  !p.is_owner && p.role === 'workspace_admin' ? (
                    <Button variant="outlined" size={28} onClick={() => void handOver(p)} disabled={m.transfer.isPending}>
                      Make owner
                    </Button>
                  ) : (
                    <div />
                  )
                ) : null}
                {/* The owner stays an administrator until they hand the workspace over. */}
                <Select value={p.role} options={ROLE_OPTIONS} onChange={(v) => m.setRole.mutate({ id: ws.public_id, userId: p.user_id, role: v as Role })} disabled={you || !admin || p.is_owner} size="sm" width={150} />
                {!you && admin && !p.is_owner ? (
                  <button
                    type="button"
                    title="Remove"
                    className="tr-hover-danger2"
                    onClick={async () => {
                      if (await confirm({ title: `Remove ${p.display_name || p.email} from ${ws.name}?`, action: 'Remove', danger: true })) m.remove.mutate({ id: ws.public_id, userId: p.user_id });
                    }}
                    style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', border: 0, borderRadius: 6, background: 'transparent', color: '#8a8a8f', cursor: 'pointer' }}
                  >
                    <IconX size={14} />
                  </button>
                ) : (
                  <div />
                )}
              </div>
            );
          })}
          {invites.map((i) => (
            <div key={i.public_id} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f0f0ee', gap: 16 }}>
              <Avatar name={i.email} size={32} pending />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 500 }}>{i.email}</span>
                  <Badge tone="amber">INVITED</Badge>
                </div>
                <span className="tr-ellipsis" style={{ fontSize: 12.5, color: '#8a8a8f' }}>{i.email}</span>
              </div>
              {ws.is_owner ? <div /> : null}
              <Select value={i.role} options={ROLE_OPTIONS} onChange={() => undefined} disabled size="sm" width={150} />
              {admin ? (
                <button
                  type="button"
                  title="Remove"
                  className="tr-hover-danger2"
                  onClick={async () => {
                    if (await confirm({ title: `Cancel the invite for ${i.email}?`, action: 'Cancel invite', danger: true })) m.revoke.mutate({ id: ws.public_id, inviteId: i.public_id });
                  }}
                  style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', border: 0, borderRadius: 6, background: 'transparent', color: '#8a8a8f', cursor: 'pointer' }}
                >
                  <IconX size={14} />
                </button>
              ) : (
                <div />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
